import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { api } from "@/lib/api";
import { formatApiError } from "@/lib/formatApiError";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { CalendarCheck, CalendarClock, CheckCircle2, Loader2, MapPin } from "lucide-react";

export default function RdvResponsePage() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [error, setError] = useState("");
  const [mode, setMode] = useState(null);
  const [message, setMessage] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  useEffect(() => {
    api.getPublicRdv(token).then((r) => setInfo(r.data)).catch((e) => setError(formatApiError(e, "Lien invalide ou expiré.")));
  }, [token]);

  const send = async (action) => {
    setBusy(true);
    setError("");
    try {
      await api.replyPublicRdv(token, { action, message: message.trim() || null });
      setDone(action);
    } catch (e) {
      setError(formatApiError(e, "Envoi impossible, réessayez."));
    } finally {
      setBusy(false);
    }
  };

  const last = info?.response;
  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <Card className="w-full max-w-md" data-testid="rdv-page">
        <CardHeader>
          <CardTitle className="font-heading text-xl">{info?.company || "Votre intervention"}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-5">
          {!info && !error && <Loader2 className="h-6 w-6 animate-spin text-primary" />}
          {error && <p className="text-sm text-destructive" data-testid="rdv-error">{error}</p>}
          {info && (
            <div className="rounded-md bg-muted/50 p-4 text-sm space-y-1" data-testid="rdv-details">
              <p>Bonjour <strong>{info.client_name}</strong>,</p>
              <p className="flex items-start gap-2"><CalendarClock className="h-4 w-4 text-primary shrink-0 mt-0.5" /><span>Intervention SAV prévue le <strong>{info.date}</strong></span></p>
              {info.address && <p className="flex items-start gap-2"><MapPin className="h-4 w-4 text-primary shrink-0 mt-0.5" /><span>{info.address}</span></p>}
            </div>
          )}
          {done ? (
            <div className="flex gap-3 rounded-md border border-emerald-200 bg-emerald-50 p-4 text-sm text-emerald-900" data-testid="rdv-done">
              <CheckCircle2 className="h-5 w-5 shrink-0" />
              <p>{done === "confirm" ? "Merci, votre rendez-vous est confirmé." : "Merci, nous revenons vers vous rapidement avec un nouveau créneau."}</p>
            </div>
          ) : info?.open ? (
            <div className="space-y-4">
              {last && (
                <p className="text-xs text-muted-foreground" data-testid="rdv-last-response">
                  Votre dernière réponse : {last.action === "confirm" ? "rendez-vous confirmé" : "demande d'un autre créneau"}.
                </p>
              )}
              {mode !== "reschedule" ? (
                <div className="grid gap-3">
                  <Button className="h-12" onClick={() => send("confirm")} disabled={busy} data-testid="rdv-confirm-btn">
                    {busy ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CalendarCheck className="h-4 w-4 mr-2" />}
                    Je confirme le rendez-vous
                  </Button>
                  <Button variant="outline" className="h-12" onClick={() => setMode("reschedule")} data-testid="rdv-reschedule-btn">
                    <CalendarClock className="h-4 w-4 mr-2" /> Je souhaite un autre créneau
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <Textarea rows={4} maxLength={1000} value={message} onChange={(e) => setMessage(e.target.value)}
                    placeholder="Vos disponibilités (ex : mardi après-midi ou jeudi matin)" data-testid="rdv-message-input" />
                  <div className="flex gap-2">
                    <Button onClick={() => send("reschedule")} disabled={busy || !message.trim()} data-testid="rdv-reschedule-submit-btn">
                      {busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />} Envoyer ma demande
                    </Button>
                    <Button variant="ghost" onClick={() => setMode(null)} data-testid="rdv-reschedule-cancel-btn">Retour</Button>
                  </div>
                </div>
              )}
            </div>
          ) : info ? (
            <p className="text-sm text-muted-foreground" data-testid="rdv-closed">
              Cette intervention n'est plus modifiable en ligne. Contactez-nous directement.
            </p>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
